import { TAuctionFormValues } from "@components";
import { Nullable } from "@utils";

export function encodeOptionValues(
  values: TAuctionFormValues,
  customDepositFile?: Nullable<{ url: string; key: string }>,
) {
  const agency = values.isOnline ? "10" : values.agency;

  function hasValue(object?: object) {
    if (!object) return false;
    return Object.keys(object).length > 0;
  }

  let deposits = values.deposits?.length
    ? values.deposits.filter(Boolean).reduce((object, { assets, deposit }) => {
        if (deposit === undefined || !assets.length) return object;
        return Object.assign(
          object,
          assets.reduce((res, asset) => {
            return Object.assign(res, { [asset as string]: deposit });
          }, {}),
        );
      }, {})
    : undefined;

  const auctioneer_fees =
    values.auctioneerFees?.length &&
    values.auctioneerFees.filter(Boolean).reduce((object, { assets, fee }) => {
      if (fee === undefined || !assets.length) return object;
      return Object.assign(
        object,
        assets.reduce((res, asset) => {
          return Object.assign(res, { [asset as string]: fee });
        }, {}),
      );
    }, {});

  const converted_stakeholders =
    values.stakeholders &&
    values.stakeholders
      .filter(({ country_geoname_id }) => country_geoname_id)
      .map((item) => ({
        country_geoname_id: +item.country_geoname_id,
        agency: item.agency,
      }));

  const payload = {
    is_online: values.isOnline,
    is_test: values.isTest,
    auction_date: values.date,
    auction_end_date: values.auctionEndDate,
    auction_time: values.auctionTime,
    auction_end_time: values.auctionEndTime,
    deposits_end_date: values.depositDate,
    deposits_end_time: values.depositDate && values.depositTime ? values.depositTime : null,
    stakeholders: converted_stakeholders,
    timezone: values.timezone,
    payment_account: values.paymentAccount,
    payment_conditions: values.paymentConditions,
    payment_conditions_fr: values.paymentConditionsFr,
    payment_conditions_es: values.paymentConditionsEs,
    payment_conditions_pt: values.paymentConditionsPt,
    pickup_details: values.pickupDetails,
    clearance_instructions: values.clearanceInstructions,
    ...(!values.isOnline && { auctioneer_fees }),
    deposits,
    agency,
    gas_strategy: values.gasStrategy ? +values.gasStrategy : undefined,
    sealed: values.sealedBid,
    conversion_rate: values.conversionRate,
    unhcr_lot_count: values.nonWfpLots,
    unhcr_services: values.unhcrServicesFee,
    auctioneer_invoice: values.auctioneerInvoice,
    co_focal_point: hasValue(values.focalPoint)
      ? {
          full_name: values.focalPoint!.fullName,
          email: values.focalPoint!.email,
          phone: values.focalPoint!.phone,
        }
      : undefined,
    gas_team_representative: hasValue(values.representative)
      ? {
          full_name: values.representative!.fullName,
          email: values.representative!.email,
          phone: values.representative!.phone,
        }
      : undefined,
    unhcr_supervisor: hasValue(values.supervisor)
      ? {
          full_name: values.supervisor!.fullName,
          email: values.supervisor!.email,
          phone: values.supervisor!.phone,
        }
      : undefined,
    location_geoname_id: values.city,
    currency_code: values.currency,
    send_email_bid_was_outbid: values.sendEmailBidWasOutbid,
    send_email_bidder_won: true, // Always send as true - field is hidden from UI
    email_language: values.emailLanguage,
    requires_online_deposits: values.requiresOnlineDeposits,
    enable_bank_transfer: values.enableBankTransfer,
    enable_online_payment: values.enableOnlinePayment,
    enable_deposit_bank_transfer: values.deposits?.length
      ? values.enableDepositBankTransfer
      : undefined,
    enable_deposit_online_payment: values.deposits?.length
      ? values.enableDepositOnlinePayment
      : undefined,
    enable_deposit_custom_payment: values.customDeposit,
    custom_deposit_name: values.customDepositName ? values.customDepositName : undefined,
    custom_deposit_file: customDepositFile,
    topic_bcc_settings: values.requestTopics?.length
      ? values.requestTopics.map((topic) => ({
          topic: topic.topic,
          bcc_gas_representative: topic.bcc_gas_representative,
          bcc_co_focal_point: topic.bcc_co_focal_point,
          additional_emails: topic.additional_emails,
        }))
      : undefined,
  };

  return payload;
}

type OptionReturnType = ReturnType<typeof encodeOptionValues>;

export function partialEncodeOptionValues(
  values: TAuctionFormValues,
  customDepositFile?: Nullable<{ url: string; key: string }>,
  fields?: string[],
) {
  const actualFields = fields ?? Object.keys(values);
  const newValues = encodeOptionValues(values, customDepositFile) as Partial<OptionReturnType>;
  delete newValues.send_email_bidder_won; // Not editable
  delete newValues.is_test; // Not editable
  if (!newValues.deposits_end_time && !actualFields.includes("depositTime")) {
    // Remove null from deposit time if it's not present in values
    delete newValues.deposits_end_time;
  }
  return newValues;
}
